import React, {useState} from 'react'
import {observer} from 'mobx-react'
import {Box, Button, Flex, Stack, Text} from '../../../vanilla'
import {
    DeliveryCardStandardIcon,
    DeliveryCardPreBookIcon,
    ArrowRightIcon,
    FeatureCheckIcon,
    RapidThumbIcon
} from '../../../components/icons'
import {RapidDeliveryETA} from '../../../components/book-delivery/rapid-delivery-eta'
import useNavigation from '../../../hooks/use-navigation'
import {
    DELIVERY_CARD_CONTENT_NAMES,
    useDeliveryCardTracking
} from '../../../analytics/select-content-tracking'
import * as styles from './delivery-box.css'

const DELIVERY_TYPE_PATHS: Record<string, string> = {
    'Standard Delivery': '/book-delivery?type=nextDay',
    'Same Day Delivery': '/book-delivery?type=sameDay',
    'Rapid Delivery': '/book-delivery?type=rapid',
    'Pre-Book Delivery': '/pre-book-delivery'
}

const getDeliveryTypeSelector = (deliveryType?: string) =>
    deliveryType?.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '')

const DELIVERY_INFO_SELECTORS: Record<string, string> = {
    'Standard Delivery': 'nextDay',
    'Same Day Delivery': 'sameDay',
    'Rapid Delivery': 'rapid',
    'Pre-Book Delivery': 'prebook'
}

interface DeliveryBoxProps {
    deliveryType?: string
    available?: boolean
    lowestCost?: number | null
    unavailableMessage?: string | null
    deliveryTypeSubtext?: string
    features: string[]
    minSpend?: number | null
}

export const DeliveryBox = observer((props: DeliveryBoxProps) => {
    const [isFeaturesOpen, setIsFeaturesOpen] = useState(false)
    const navigate = useNavigation()
    const sendDeliveryCardSelectContent = useDeliveryCardTracking()
    const {
        deliveryType,
        available = true,
        lowestCost,
        unavailableMessage,
        deliveryTypeSubtext,
        features,
        minSpend
    } = props

    const handleBookSlot = () => {
        if (!available) {
            return
        }

        const path = deliveryType ? DELIVERY_TYPE_PATHS[deliveryType] : undefined
        const contentKey = DELIVERY_INFO_SELECTORS[deliveryType || '']
        const contentName = contentKey
            ? DELIVERY_CARD_CONTENT_NAMES[contentKey as keyof typeof DELIVERY_CARD_CONTENT_NAMES]
            : undefined

        if (contentName && path) {
            sendDeliveryCardSelectContent({contentName, contentUrl: path})
        }

        if (path) {
            navigate(path, 'push')
        }
    }

    const deliveryIcons = {
        'Standard Delivery': DeliveryCardStandardIcon,
        'Same Day Delivery': DeliveryCardPreBookIcon,
        'Rapid Delivery': RapidThumbIcon,
        'Pre-Book Delivery': DeliveryCardPreBookIcon
    }

    const DeliveryIcon = deliveryType
        ? deliveryIcons[deliveryType as keyof typeof deliveryIcons] || null
        : null
    const deliveryTypeSelector = getDeliveryTypeSelector(deliveryType)
    const deliveryInfoSelector = DELIVERY_INFO_SELECTORS[deliveryType || '']

    const featureContent = features.map((feature, index) => (
        <Flex key={index} gap="8px">
            <FeatureCheckIcon aria-hidden="true" flexShrink="0" marginTop="4px" />
            <Text variant="text1">{feature}</Text>
        </Flex>
    ))

    const mobileBookButton = (
        <Box display={['flex', 'none']} justifyContent="flex-end">
        <Button
            variant="unstyled"
            disabled={!available}
            aria-label={available ? 'Book a Slot' : 'Not available right now'}
            data-test-selector={`delivery-box-${deliveryTypeSelector}-book-slot-mobile`}
            padding="12px"
            width="24px"
            height="24px"
            border="2px"
            borderColor="accent1"
            color="accent1"
            onClick={handleBookSlot}
            style={{
                boxSizing: 'content-box',
                borderRadius: '50%'
            }}
        >
            <ArrowRightIcon width="24px" height="24px" color="accent1" aria-hidden="true" />
        </Button>
        </Box>
    )

    return (
    <Box
        flex="1"
        backgroundColor={available ? 'white' : 'transparent'}
        data-test-selector={`delivery-box-${deliveryTypeSelector}`}
        padding="16px"
        display="flex"
        flexDirection="column"
        border="1px"
        borderColor="gray100"
        >
        <Box
            marginBottom="16px"
            display="flex"
            flexDirection={['row', 'column']}
            alignItems={['center', 'stretch']}
            gap={['12px', '0px']}
            position="relative"
        >
            {DeliveryIcon && (
            <DeliveryIcon marginBottom={['0px', '16px']} boxSize="56px" aria-hidden="true" />
            )}
            <Box flex="1">
            <Text variant="heading1SemiBold" data-test-selector={`delivery-box-${deliveryTypeSelector}-title`}>{deliveryType}</Text>
            <Text variant="text3">{deliveryTypeSubtext}</Text>
            </Box>
            {deliveryType === 'Rapid Delivery' && (
            <Text
                position="absolute"
                top="0px"
                right="0px"
                paddingX="6px"
                color="white"
                as="span"
                borderRadius="base"
                display="inline"
                variant="text1"
                backgroundColor="accent5"
            >
                Fastest
            </Text>
            )}
        </Box>

        {unavailableMessage ? (
            <Box
                display="flex"
                flexDirection="row"
                justifyContent="space-between"
                alignItems="center"
                gap="12px"
                marginBottom={['0px', '16px']}
                data-test-selector={`delivery-box-${deliveryTypeSelector}-unavailable`}
            >
                <Text variant="text2Bold">{unavailableMessage}</Text>
                {mobileBookButton}
            </Box>
        ) : (
            <>
                <Box display={['block', 'none']}>
                    <Button
                        variant="unstyled"
                        paddingX="0px"
                        style={{fontWeight: 300}}
                        color="accent5"
                        textDecoration="none"
                        justifyContent="flex-start"
                        onClick={() => setIsFeaturesOpen((isOpen) => !isOpen)}
                        aria-expanded={isFeaturesOpen}
                        data-test-selector={`delivery-box-${deliveryTypeSelector}-more-info`}
                    >
                        {isFeaturesOpen ? 'Less Info -' : 'More Info +'}
                    </Button>
                </Box>

                {isFeaturesOpen && (
                    <Box
                        display={['block', 'none']}
                        backgroundColor="gray75"
                        borderRadius="base"
                        padding="8px"
                        data-test-selector={`delivery-info-${deliveryInfoSelector}-delivery`}
                    >
                        {featureContent}
                    </Box>
                )}
                <Stack
                    display={['none', 'flex']}
                    spacing="0px"
                    data-test-selector={`delivery-info-${deliveryInfoSelector}-delivery`}
                >
                    {featureContent}
                </Stack>

                <Box
                    display="flex"
                    flexDirection="row"
                    justifyContent="space-between"
                    alignItems="center"
                    marginY={[available ? '0px' : '16px', '16px']}
                >
                    <Stack
                        spacing="0px"
                        data-test-selector={`delivery-fee-${deliveryInfoSelector}-delivery`}
                    >
                    {lowestCost !== null && lowestCost !== undefined && (
                        <Text variant="unstyled" size="xs" weight="semibold">
                        {lowestCost === 0 ? 'From FREE' : `From £${lowestCost.toFixed(2)}`}
                        </Text>
                    )}
                    {deliveryType === 'Rapid Delivery' ? (
                        <Box className={styles.rapidDeliveryEta}>
                            <RapidDeliveryETA variant="bookDelivery" />
                        </Box>
                    ) : (
                        minSpend && deliveryType !== 'Pre-Book Delivery' ? (
                        <Text variant="unstyled" size="xs" weight="normal">
                            Min spend £{minSpend}.
                        </Text>
                        ) : null
                    )}
                    </Stack>
                    {available && mobileBookButton}
                </Box>
            </>
        )}

        <Stack marginTop="auto">
            <Box display={[available ? 'none' : 'block', 'block']} width="full">
            <Button
                width="full"
                variant={available ? 'outlinePrimary' : 'dark'}
                disabled={!available}
                onClick={handleBookSlot}
                data-test-selector={`delivery-box-${deliveryTypeSelector}-book-slot-desktop`}
            >
                {available ? 'Book a Slot' : 'Not available right now'}
            </Button>
            </Box>
        </Stack>
        </Box>
    )
})
